// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { auth, googleProvider, facebookProvider, db } from '../firebase';
// Remove unused imports: isUserAdmin, isApprovedServiceProvider
import { getUserRole, USER_ROLES, APPLICATION_STATUS } from '../services/UserRolesService';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

// Create the auth context
const AuthContext = createContext();

// Hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Provider component to wrap your app
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isApprovedProvider, setIsApprovedProvider] = useState(false);
  const [loading, setLoading] = useState(true);

  // Sign in with Google and save profile info to Firestore
  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);

      // Save user profile info to Firestore
      const userRef = doc(db, 'users', result.user.uid);
      const userDoc = await getDoc(userRef);

      let userData = {
        displayName: result.user.displayName || 'Unnamed User',
        email: result.user.email,
        photoURL: result.user.photoURL,
        updatedAt: serverTimestamp()
      };

      if (!userDoc.exists()) {
        // Only set role, status and creation timestamp for new users
        userData.role = USER_ROLES.USER;
        userData.status = null; // Initially no provider status
        userData.createdAt = serverTimestamp();
      } else {
        // Ensure essential fields are preserved/updated if they exist
        userData = {
            ...userDoc.data(), // Keep existing data like role, status, createdAt
            ...userData // Overwrite with new login details
        };
      }

      // Use setDoc with merge to create or update safely
      await setDoc(userRef, userData, { merge: true });

      return result;
    } catch (error) {
      console.error("Error signing in with Google:", error);
      throw error;
    }
  };

  // Sign in with Facebook and save profile info to Firestore
  const signInWithFacebook = async () => {
    try {
      const result = await signInWithPopup(auth, facebookProvider);

      // Save user profile info to Firestore
      const userRef = doc(db, 'users', result.user.uid);
      const userDoc = await getDoc(userRef);

      let userData = {
        displayName: result.user.displayName || 'Unnamed User',
        email: result.user.email,
        photoURL: result.user.photoURL,
        updatedAt: serverTimestamp()
      };

      if (!userDoc.exists()) {
        // Only set role, status and creation timestamp for new users
        userData.role = USER_ROLES.USER;
        userData.status = null;
        userData.createdAt = serverTimestamp();
      } else {
         // Ensure essential fields are preserved/updated if they exist
        userData = {
            ...userDoc.data(),
            ...userData
        };
      }

      // Use setDoc with merge to create or update safely
      await setDoc(userRef, userData, { merge: true });

      return result;
    } catch (error) {
      console.error("Error signing in with Facebook:", error);
      throw error;
    }
  };

  // Sign up with email and password and save to Firestore
  const signup = async (email, password, displayName) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      // Update profile with display name
      await updateProfile(result.user, { displayName });

      // Save user profile info to Firestore
      const userRef = doc(db, 'users', result.user.uid);
      await setDoc(userRef, {
        displayName,
        email,
        role: USER_ROLES.USER, // Default role
        status: null, // Default status
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return result;
    } catch (error) {
      console.error("Error signing up:", error);
      throw error;
    }
  };

  // Sign in with email and password
  const login = async (email, password) => {
    try {
       const result = await signInWithEmailAndPassword(auth, email, password);
       // Optionally trigger an update to ensure latest info is synced, though onAuthStateChanged usually handles this
       const userRef = doc(db, 'users', result.user.uid);
       await setDoc(userRef, { updatedAt: serverTimestamp() }, { merge: true });
       return result;
    } catch (error) {
      console.error("Error signing in:", error);
      throw error;
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      return await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error("Error resetting password:", error);
      throw error;
    }
  };

  // Sign out function
  const logout = async () => {
    try {
      await signOut(auth);
      // Reset states immediately on logout
      setCurrentUser(null);
      setUserRole(null);
      setIsAdmin(false);
      setIsApprovedProvider(false);
      return true;
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  };

  // Load user role information
  const loadUserRoleAndPermissions = async (user) => {
    if (!user) {
      setUserRole(null);
      setIsAdmin(false);
      setIsApprovedProvider(false);
      return;
    }

    try {
      const roleData = await getUserRole(user.uid);
      setUserRole(roleData); // roleData contains { role: '...', status: '...' }

      // Check if user is admin based on the role field
      const adminStatus = roleData.role === USER_ROLES.ADMIN;
      setIsAdmin(adminStatus);

      // Check if user is approved service provider (provider role + approved status OR admin role + approved status)
      const providerStatus = (
        (roleData.role === USER_ROLES.SERVICE_PROVIDER || roleData.role === USER_ROLES.ADMIN) &&
        roleData.status === APPLICATION_STATUS.APPROVED
      );
      setIsApprovedProvider(providerStatus);

    } catch (error) {
      console.error("Error loading user role:", error);
      setUserRole({ role: USER_ROLES.USER, status: null });
      setIsAdmin(false);
      setIsApprovedProvider(false);
    }
  };

  // Set up auth state listener
  useEffect(() => {
    setLoading(true); // Start loading when listener is set up
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user); // Update currentUser immediately

      if (user) {
        await loadUserRoleAndPermissions(user); // Load roles and permissions
      } else {
        // Clear roles and permissions if no user
        setUserRole(null);
        setIsAdmin(false);
        setIsApprovedProvider(false);
      }

      setLoading(false); // Finish loading after user and roles are processed
    });

    // Clean up the listener
    return unsubscribe;
  }, []);

  // Function to manually refresh user permissions (useful after role changes)
  const refreshUserPermissions = async () => {
    if (currentUser) {
      await loadUserRoleAndPermissions(currentUser);
    }
  };

  // Context value
  const value = {
    currentUser,
    userRole,
    isAdmin,
    isApprovedProvider,
    signInWithGoogle,
    signInWithFacebook,
    signup,
    login,
    resetPassword,
    logout,
    refreshUserPermissions,
    loading // Keep loading state available for consumers like AdminUsers
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};